import React, { useLayoutEffect, useRef, useState } from 'react';

import {
  onlyDigits,
  formatPhone,
  toPhoneDigits,
  validatePhone,
  phoneMessage,
} from '../../functions/phone';

// DTGD-1780: máscara dinâmica (fixo/celular) + aviso de validade no campo.
// O onChange recebe só dígitos, no formato de evento que os handleChange das
// telas já esperam ({ target: { id, value } }).
function PhoneField({
  id,
  label,
  value,
  onChange,
  onBlur,
  disabled,
  touched,
  placeholder,
  mobileOnly = false,
  optional = false,
}) {
  const rules = { mobileOnly, required: !optional };
  const digits = onlyDigits(value);
  const { valid } = validatePhone(digits, rules);
  // "Telefone válido" só depois que o usuário mexe no número; o aviso de
  // inválido aparece sempre (inclusive para número já cadastrado)
  const [changed, setChanged] = useState(false);
  const showFeedback = digits
    ? !valid || changed
    : Boolean(touched) && !optional;

  const inputRef = useRef(null);
  // quantos dígitos ficam antes do cursor; reposiciona depois de reformatar,
  // senão o React joga o cursor para o fim ao editar no meio do número
  const digitsBeforeCaret = useRef(null);
  const formatted = formatPhone(digits);

  useLayoutEffect(() => {
    const input = inputRef.current;
    const count = digitsBeforeCaret.current;
    digitsBeforeCaret.current = null;
    if (count === null || !input || input !== document.activeElement) return;

    let pos = 0;
    for (let seen = 0; pos < formatted.length && seen < count; pos += 1) {
      if (/\d/.test(formatted[pos])) seen += 1;
    }
    input.setSelectionRange(pos, pos);
  }, [formatted]);

  const handleChange = event => {
    const { value: typed, selectionStart } = event.target;
    const editingMiddle =
      selectionStart !== null && selectionStart < typed.length;
    const before = editingMiddle
      ? onlyDigits(typed.slice(0, selectionStart)).length
      : null;
    const typedDigits = onlyDigits(typed);
    let next = toPhoneDigits(typed);
    digitsBeforeCaret.current = before;

    if (
      editingMiddle &&
      typedDigits === digits &&
      typed.length < formatted.length
    ) {
      // apagou um separador ("-", ")" ou espaço): apaga o dígito anterior
      if (before === 0) return;
      next = digits.slice(0, before - 1) + digits.slice(before);
      digitsBeforeCaret.current = before - 1;
    } else if (
      editingMiddle &&
      digits.length === 11 &&
      typedDigits.length === 12
    ) {
      // campo cheio: inserir no meio não pode empurrar dígitos para fora
      return;
    }

    setChanged(true);
    onChange({ target: { id, value: next } });
  };

  return (
    <div className="form-group auth-form-group-custom mb-4">
      <label htmlFor={id}>{label}</label>
      <input
        type="tel"
        inputMode="numeric"
        id={id}
        ref={inputRef}
        placeholder={placeholder}
        value={formatted}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        className={
          showFeedback && !valid
            ? 'form-control text-input error'
            : 'form-control text-input'
        }
      />
      {showFeedback && (
        <div className={`${valid ? 'valid' : 'invalid'}-feedback d-block`}>
          {phoneMessage(digits, rules)}
        </div>
      )}
    </div>
  );
}

export default PhoneField;
